/**
 * PaperUsageTab — 문제 상세 [시험지] 탭. **이 문제가 담긴 신규 시험지**를 보여 준다.
 *
 * 데이터는 서버 역조회 한 번이다(`paperUsageApi.js` — `GET /papers?problem_id=`,
 * 계약 `docs/problem-hub-v2/BE-20260901-reply-paper-usage-by-problem.md`). 2026-09-01 이전에는 브라우저가
 * 시험지를 하나씩 열어 대조했고, 그래서 이 화면에 있던 「비용을 먼저 말한다·훑은 시각을 말한다·덜 본 것을
 * 덜 봤다고 말한다」 셋은 계약과 함께 걷었다. 남은 것은 둘 — ① 배포 전 서버면 목록 대신 **오류로** 선다
 * (전체 목록을 「전부 들어 있다」로 그리지 않기 위해, paperUsageApi 주석). ② 이 탭은 문제가 바뀔 때만
 * 리마운트되므로(ProblemHub `key={p-${id}}`) 시험지를 고치고 돌아오면 낡을 수 있다 — [새로고침]을 곁에 둔다.
 *
 * 결과는 **시험지 관리(`/exam-papers`)의 표를 그대로** 쓴다(`PaperTable`) — 같은 시험지가 화면에 따라
 * 다른 얼굴이면 같은 것인 줄 모른다. 이 자리에서만 「이 문제」(몇 번 문항인가)·「배포」(몇 번 나갔나) 두 칸이
 * 더 서고, 이동·복사·삭제가 없으니 「작업」 칸은 서지 않는다(오너 지시 2026-09-01 「시험지 표를 그대로」).
 *
 * 머리를 누르면 줄이 선다(내림차순 → 오름차순 → 기본). 이 탭은 목록을 통째로 받아서 브라우저 안의 정렬이
 * 곧 전체 차례다 — 시험지 관리 목록과 달리 머리 정렬을 켤 수 있는 이유다(PaperTable 머리 주석).
 * 기본은 서버가 준 차례 — 수정일 최근순이다(paperUsageApi).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PaperTable, { paperIdOf, paperSortValue } from '../ExamPapersV2/PaperTable';
import { buildFolderPathIndex } from '../ExamPapersV2/examPaperApi';
import { nextSort, sortRows, numSortValue } from '../common/tableSort';
import { fetchPapersForProblem, matchedLabelOfRow } from './paperUsageApi';

function PaperUsageTab({ problemId, onOpenPaper = null }) {
  const { getAccessToken } = useAuth();
  const [data, setData] = useState(null); // { rows, folders, totalItems, truncated }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (problemId == null) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchPapersForProblem(problemId, getAccessToken)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (!alive) return;
        // 실패하면 지난 결과도 내린다 — 오류 띠 옆에 옛 목록이 남아 있으면 그것이 지금 것으로 읽힌다.
        setData(null);
        setError(e?.message || '이 문제가 담긴 시험지를 불러오지 못했습니다.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [problemId, reloadKey, getAccessToken]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const papers = useMemo(() => rows.map((r) => r.paper), [rows]);
  // 표는 시험지만 받는다 — 「이 문제가 몇 번인가」「몇 번 나갔나」는 시험지 번호로 되짚어 칸에 넣어 준다
  const rowById = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(String(paperIdOf(r.paper)), r));
    return m;
  }, [rows]);
  const matchedLabelOf = useCallback((p) => matchedLabelOfRow(rowById.get(String(paperIdOf(p)))), [rowById]);
  // 「배포」 칸은 서버가 assign_count 를 실어 줄 때만 선다(요청서 §2-3 「없으면 그 칸을 그리지 않습니다」).
  // 0회는 0회다 — '—'(값이 안 옴)와 같은 글자로 뭉개지 않는다.
  const withAssign = rows.some((r) => r.assignCount != null);
  const assignLabelOf = useCallback((p) => {
    const hit = rowById.get(String(paperIdOf(p)));
    const n = Number(hit?.assignCount);
    if (hit == null || hit.assignCount == null || !Number.isInteger(n)) return null;
    return n === 0 ? <span className="text-gray-400">미배포</span> : `${n}회`;
  }, [rowById]);

  // 머리 정렬 — [새로고침]으로 목록이 다시 와도 고른 칸은 남는다. 문제가 바뀌면 탭째 새로 선다.
  const [sort, setSort] = useState(null);
  const onSort = useCallback((key) => setSort((prev) => nextSort(prev, key)), []);
  // 폴더 칸과 같은 경로 글자로 세운다 — 표도 같은 folders 로 같은 지도를 만든다
  const folderPaths = useMemo(() => buildFolderPathIndex(data?.folders || []), [data]);
  const sortedPapers = useMemo(() => sortRows(papers, sort, (p, key) => {
    const hit = rowById.get(String(paperIdOf(p)));
    // 「이 문제」는 몇 번 문항인가(0부터라도 차례는 같다) · 「배포」는 나간 횟수 — 0회(미배포)도 값이다
    if (key === 'matched') return numSortValue(hit?.matchedOrderNo);
    if (key === 'assign') return numSortValue(hit?.assignCount);
    return paperSortValue(p, key, folderPaths);
  }), [papers, sort, rowById, folderPaths]);

  if (problemId == null) {
    return <div className="p-6 text-sm text-gray-400">문제를 선택하면 담긴 시험지를 찾을 수 있습니다.</div>;
  }

  return (
    <div className="space-y-3 p-1">
      {error && (
        <div className="flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-sm text-red-600">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="inline-flex shrink-0 items-center gap-1 text-xs text-red-500 underline transition-colors hover:text-red-700 disabled:opacity-50"
          >
            <RefreshCw className="size-3" />
            다시 시도
          </button>
        </div>
      )}

      {loading && data == null && !error && (
        <p className="text-xs text-gray-400">이 문제가 담긴 시험지를 찾는 중…</p>
      )}

      {data != null && (
        <>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
            <span>이 문제가 담긴 시험지 {data.totalItems}장</span>
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="inline-flex items-center gap-1 text-gray-500 underline transition-colors hover:text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`size-3 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </button>
          </div>

          {data.truncated && (
            <div className="rounded-2xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-700">
              시험지가 {data.totalItems}장이라 최근 {rows.length}장까지만 받았습니다.
            </div>
          )}

          <PaperTable
            items={sortedPapers}
            sort={sort}
            onSort={onSort}
            /* 0건이어도 표 머리를 남긴다 — 시험지 관리와 같은 규칙(PaperTable 주석) */
            empty={<p className="text-sm text-gray-400">이 문제를 담은 시험지가 없습니다.</p>}
            /* 폴더 칸이 시험지 관리와 같은 「부모 › 자식」 경로를 그리게 하는 재료 */
            folders={data.folders || []}
            matchedLabelOf={matchedLabelOf}
            assignLabelOf={withAssign ? assignLabelOf : null}
            onSelect={onOpenPaper}
          />
        </>
      )}
    </div>
  );
}

export default PaperUsageTab;
